import {
  trackTranslationReverted,
  trackTranslationWordEdited,
} from '@src/analytics/EventTracker';
import { useStore, useTranslationStore } from '@src/stores-v2/StoreContext';
import React from 'react';

import { isEdited, resolveOriginalValue } from '../translatedPhraseEditing';

import type { TranslationPhrase } from '@src/types.ts/translation';

export interface ContextMenuState {
  phrase: TranslationPhrase;
  anchorElement: HTMLElement;
  positionXY: { x: number; y: number };
  /**
   * PLAT-10646 — the source element range (current array positions) the
   * right-clicked phrase / covering selection maps to, resolved at
   * menu-request time (the selection can collapse once the menu opens).
   * Only supplied by surfaces that host the extended translated context
   * menu; null when the mapped span has no surviving source words.
   */
  sourceRange?: { startIndex: number; endIndex: number } | null;
}

export interface EditingState {
  phrase: TranslationPhrase;
  anchorElement: HTMLElement;
}

export interface UsePhraseEditingResult {
  contextMenu: ContextMenuState | null;
  editing: EditingState | null;

  /** Current in-flight value for the editing popover's textarea seed. */
  editingCurrentValue: string;

  /**
   * The machine baseline for the editing popover — its read-only "Original
   * translation" row and the Revert target. Defined for override fragments
   * too (joined from the retired `supersededBy` siblings), so Revert enables
   * there; undefined when the baseline can't be resolved.
   */
  editingOriginalValue: string | undefined;

  /**
   * Whether the editing phrase has an applied edit to revert (the shared
   * `isEdited` predicate — applied translation differs from the machine
   * baseline). Drives the popover's Revert enabled state.
   */
  editingIsEdited: boolean;

  /** Open the context menu at cursor for the given phrase + anchor element. */
  handleContextMenuRequest: (
    phrase: TranslationPhrase,
    anchorElement: HTMLElement,
    positionXY: { x: number; y: number },
    sourceRange?: { startIndex: number; endIndex: number } | null,
  ) => void;

  /** Menu "Edit" action — promotes the current menu target to editing. */
  handleMenuEdit: () => void;

  /**
   * Open the editor popover directly for a phrase (no context menu). The
   * review workspace clicks a changed word straight into the editor (whose
   * "Revert to original" is the Undo), instead of a separate hover tooltip.
   */
  handleOpenEditor: (
    phrase: TranslationPhrase,
    anchorElement: HTMLElement,
  ) => void;

  /** Menu dismissed via click-outside or parent state change. */
  handleMenuClose: () => void;

  /** Commit the popover input value; closes the popover. */
  handleSave: (phrase: TranslationPhrase, value: string) => void;

  /** Close the popover without committing. */
  handleCancel: () => void;

  /**
   * Revert the phrase to its captured machine original. Popover stays
   * open so the user can see the result and keep / cancel from there.
   */
  handleRevert: (phrase: TranslationPhrase) => void;

  /**
   * Revert by the phrase's stable source-word ids — the changed-phrase hover
   * Undo (PLAT-10265), migrated to stable ids (PLAT-10268). Same store +
   * analytics path as `handleRevert`, for callers that hold a phrase's
   * `originalElementIds` but not the `TranslationPhrase` object.
   */
  handleRevertByElementIds: (originalElementIds: string[]) => void;

  /**
   * Revert a whole span-replace override segment — the changed-phrase hover Undo
   * on an override fragment (PLAT-10265). An override's fragments have no
   * per-phrase machine original to revert to, so undo drops the override segment
   * (`revertOverrideSegment`) and restores the retired machine fragments.
   */
  handleRevertOverride: (segmentId: string) => void;
}

/**
 * Owns the right-click menu + popover editor state for the translated
 * transcript view. Separates the "which phrase is being targeted" state
 * machine from the rendering layer so the view component can stay focused
 * on layout + mapping.
 *
 * Reset rules:
 *   - Language swap closes both menu and popover (phrase refs become
 *     stale against the new dataset).
 *   - `editable` flipping off closes both (consumer-driven force-cancel).
 *   - NOT reset on translation-data mutation; `revertTranslation`
 *     rewrites the translation map entry in place, and closing on every
 *     write would kick the user out of the popover right after they
 *     clicked Revert.
 */
export function usePhraseEditing(
  language: string,
  editable: boolean,
): UsePhraseEditingResult {
  const translationStore = useTranslationStore();
  const rootStore = useStore();
  const [editing, setEditing] = React.useState<EditingState | null>(null);
  const [contextMenu, setContextMenu] = React.useState<ContextMenuState | null>(
    null,
  );

  React.useEffect(() => {
    setEditing(null);
    setContextMenu(null);
  }, [language]);

  React.useEffect(() => {
    if (!editable) {
      setEditing(null);
      setContextMenu(null);
    }
  }, [editable]);

  const handleContextMenuRequest = React.useCallback(
    (
      phrase: TranslationPhrase,
      anchorElement: HTMLElement,
      positionXY: { x: number; y: number },
      sourceRange?: { startIndex: number; endIndex: number } | null,
    ) => setContextMenu({ phrase, anchorElement, positionXY, sourceRange }),
    [],
  );

  const handleMenuEdit = React.useCallback(() => {
    setContextMenu(current => {
      if (!current) return null;
      setEditing({
        phrase: current.phrase,
        anchorElement: current.anchorElement,
      });
      return null;
    });
  }, []);

  const handleMenuClose = React.useCallback(() => setContextMenu(null), []);

  const handleOpenEditor = React.useCallback(
    (phrase: TranslationPhrase, anchorElement: HTMLElement) => {
      setContextMenu(null);
      setEditing({ phrase, anchorElement });
    },
    [],
  );

  const handleSave = React.useCallback(
    (phrase: TranslationPhrase, value: string) => {
      translationStore.editTranslatedPhrase(
        language,
        phrase.originalIndices,
        value,
      );
      trackTranslationWordEdited({
        story_id: rootStore.storyId ?? undefined,
        language,
      });
      setEditing(null);
    },
    [language, rootStore, translationStore],
  );

  const handleCancel = React.useCallback(() => setEditing(null), []);

  const handleRevertByElementIds = React.useCallback(
    (originalElementIds: string[]) => {
      translationStore.revertTranslation(language, originalElementIds);
      trackTranslationReverted({
        story_id: rootStore.storyId ?? undefined,
        language,
      });
    },
    [language, rootStore, translationStore],
  );

  const handleRevertOverride = React.useCallback(
    (segmentId: string) => {
      // Async save; fire-and-forget like the other revert handlers (the view
      // re-renders reactively when the store mutates).
      void translationStore.revertOverrideSegment(language, segmentId);
      trackTranslationReverted({
        story_id: rootStore.storyId ?? undefined,
        language,
      });
    },
    [language, rootStore, translationStore],
  );

  const handleRevert = React.useCallback(
    (phrase: TranslationPhrase) => {
      // A span-replace OVERRIDE fragment (parentId `o_…`) has no per-phrase
      // machine original; its baseline lives on the retired `supersededBy`
      // fragments, so revert must drop the whole override segment. But a plain
      // backend split-parent FRAGMENT also carries a `parentId` (`p_…`), and
      // that parent has no `parentPhrases[]` override record — routing it to
      // `revertOverrideSegment` logs "unknown segment p_…" and silently no-ops
      // (the `revertOverrideSegment: unknown segment p_0` bug, PLAT-10611 F4 /
      // Denys 2026-07-07). Route to the override revert ONLY for a REAL override
      // (`parentId` starts `o_`, the same opaque disambiguation used at
      // `translationSpanEdit.ts:195` / `changedPhraseKit.ts:135`); every other
      // phrase — incl. a `p_…` backend-parent child — takes the phrase-record
      // revert path.
      if (phrase.parentId?.startsWith('o_')) {
        handleRevertOverride(phrase.parentId);
      } else {
        handleRevertByElementIds(phrase.originalElementIds ?? []);
      }
      // Close the editor after reverting — revert reflects the APPLIED change,
      // not an in-progress draft, so there's nothing left to compare in the
      // popover. Mirrors the highlight flow, which also closes on revert.
      setEditing(null);
    },
    [handleRevertByElementIds, handleRevertOverride],
  );

  // Current value for the popover: any pending edit wins over the stored
  // translatedValue so the user sees their in-flight work if they reopen.
  // PLAT-10611 (F4) — resolved by the phrase's STABLE element ids (id-only
  // identity), falling back to the phrase's own `translatedValue` for id-less
  // legacy phrases.
  const editingCurrentValue = editing
    ? (translationStore.getTranslatedPhraseByElementIds(
        language,
        editing.phrase.originalElementIds,
      ) ?? editing.phrase.translatedValue)
    : '';

  // The machine baseline shown read-only + used as the Revert target. For an
  // override fragment (parentId set) resolveOriginalValue reads the segment's
  // retired (supersededBy) fragments from the live phrase list; plain phrases
  // resolve from their own record fields (the phrase list is then unused).
  const editingOriginalValue = editing
    ? resolveOriginalValue(
        editing.phrase,
        translationStore.getTranslation(language)?.phrases,
      )
    : undefined;

  // Whether the editing phrase has an applied edit to revert — the shared
  // `isEdited` predicate (applied translation differs from the machine
  // baseline). Drives the popover's Revert enabled state so it tracks the
  // applied change, not the draft in the textarea.
  const editingIsEdited = editing
    ? isEdited(
        editing.phrase,
        translationStore.getTranslation(language)?.phrases,
      )
    : false;

  return {
    contextMenu,
    editing,
    editingCurrentValue,
    editingOriginalValue,
    editingIsEdited,
    handleContextMenuRequest,
    handleMenuEdit,
    handleMenuClose,
    handleOpenEditor,
    handleSave,
    handleCancel,
    handleRevert,
    handleRevertByElementIds,
    handleRevertOverride,
  };
}
